import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { auth, db } from "../firebase";
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  orderBy
} from "firebase/firestore";
import { followUser, unfollowUser } from "../services/FollowService";
import { FaEdit, FaCamera, FaCalendarAlt, FaLink, FaMapMarkerAlt, FaHeart, FaComment } from "react-icons/fa";
import { useAuth } from "../contexts/AuthContext";
import styles from "../styles/Profile.module.css";
import { ScrollReveal } from "../hooks/useScrollAnimation";

// Helper function to extract roll number from email
const getRollNumberFromEmail = (email) => {
  if (!email) return "";
  return email.split('@')[0];
};

// Cloudinary configuration
const CLOUDINARY_CLOUD_NAME = 'dioeq3s5r';
const CLOUDINARY_UPLOAD_PRESET = 'ml_default';

export default function Profile() {
  const { email } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [mediaPosts, setMediaPosts] = useState([]);
  const [likedPosts, setLikedPosts] = useState([]);
  const [isFollowing, setIsFollowing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("posts");
  const [error, setError] = useState(null);
  const [showFollowers, setShowFollowers] = useState(false);
  const [showFollowing, setShowFollowing] = useState(false);
  const [followersList, setFollowersList] = useState([]);
  const [followingList, setFollowingList] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isEditingBio, setIsEditingBio] = useState(false);
  const [newBio, setNewBio] = useState("");
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileData, setProfileData] = useState({
    displayName: "",
    website: "",
    location: "",
    bio: ""
  });
  const fileInputRef = useRef(null);
  const { currentUser } = useAuth();

  // Fetch profile data
  useEffect(() => {
    if (!email || !currentUser) return;

    const fetchProfile = async () => {
      setLoading(true);
      setError(null);
      const decodedEmail = decodeURIComponent(email);

      try {
        // Fetch user profile
        const userQuery = query(
          collection(db, "users"),
          where("email", "==", decodedEmail)
        );
        const querySnap = await getDocs(userQuery);

        if (querySnap.empty) {
          throw new Error("User not found");
        }

        const userDoc = querySnap.docs[0];
        const userData = {
          id: userDoc.id,
          ...userDoc.data()
        };
        setProfile(userData);
        setNewBio(userData.bio || "");
        setProfileData({
          displayName: userData.displayName || "",
          website: userData.website || "",
          location: userData.location || "",
          bio: userData.bio || ""
        });

        // Check follow status
        if (currentUser.uid !== userDoc.id) {
          const currentUserRef = doc(db, "users", currentUser.uid);
          const currentUserSnap = await getDoc(currentUserRef);
          setIsFollowing(
            currentUserSnap.data()?.following?.includes(userDoc.id) || false
          );
        }

        // Fetch user's posts
        const postsQuery = query(
          collection(db, "posts"),
          where("authorUid", "==", userDoc.id),
          orderBy("createdAt", "desc")
        );

        const postsSnap = await getDocs(postsQuery);
        const userPosts = postsSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate()
        }));
        setPosts(userPosts);

        // Filter media posts (posts with files)
        const mediaOnlyPosts = userPosts.filter(post =>
          post.files && post.files.length > 0
        );
        setMediaPosts(mediaOnlyPosts);

        // Fetch user's liked posts
        await fetchLikedPosts(userDoc.id);

        // Fetch followers and following lists
        await fetchUserLists(userDoc.id);

      } catch (err) {
        console.error("Profile load error:", err);
        setError(err.message.includes('not found') ? "User not found" : "Error loading profile");
      } finally {
        setLoading(false);
      }
    };

    const fetchLikedPosts = async (userId) => {
      try {
        // Query all posts where likes array contains userId
        const likedPostsQuery = query(
          collection(db, "posts"),
          where("likes", "array-contains", userId),
          orderBy("createdAt", "desc")
        );

        const likedSnap = await getDocs(likedPostsQuery);
        const likedPostsData = likedSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate()
        }));
        setLikedPosts(likedPostsData);
      } catch (err) {
        console.error("Error fetching liked posts:", err);
        // If the query fails (might be due to composite index), fallback to client-side filtering
        await fetchAllPostsForLikes(userId);
      }
    };

    const fetchAllPostsForLikes = async (userId) => {
      try {
        // Fallback: Get all posts and filter client-side
        const allPostsQuery = query(
          collection(db, "posts"),
          orderBy("createdAt", "desc")
        );

        const allPostsSnap = await getDocs(allPostsQuery);
        const allPosts = allPostsSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate()
        }));

        // Filter posts where userId is in likes array
        const userLikedPosts = allPosts.filter(post =>
          post.likes && post.likes.includes(userId)
        );
        setLikedPosts(userLikedPosts);
      } catch (err) {
        console.error("Error in fallback liked posts fetch:", err);
      }
    };

    const fetchUserLists = async (userId) => {
      try {
        const userRef = doc(db, "users", userId);
        const userSnap = await getDoc(userRef);

        if (userSnap.exists()) {
          const data = userSnap.data();

          // Fetch followers
          if (data.followers && data.followers.length > 0) {
            const followers = await Promise.all(
              data.followers.map(async (followerId) => {
                const followerRef = doc(db, "users", followerId);
                const followerSnap = await getDoc(followerRef);
                return followerSnap.exists() ? {
                  id: followerSnap.id,
                  ...followerSnap.data()
                } : null;
              })
            );
            setFollowersList(followers.filter(Boolean));
          }

          // Fetch following
          if (data.following && data.following.length > 0) {
            const following = await Promise.all(
              data.following.map(async (followingId) => {
                const followingRef = doc(db, "users", followingId);
                const followingSnap = await getDoc(followingRef);
                return followingSnap.exists() ? {
                  id: followingSnap.id,
                  ...followingSnap.data()
                } : null;
              })
            );
            setFollowingList(following.filter(Boolean));
          }
        }
      } catch (err) {
        console.error("Error fetching user lists:", err);
      }
    };

    fetchProfile();
  }, [email, currentUser]);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !currentUser || !profile) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setError('Please upload a valid image (JPEG, PNG, GIF, WebP)');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size should be less than 5MB');
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
      formData.append('cloud_name', CLOUDINARY_CLOUD_NAME);
      formData.append('folder', 'staco_profile_images');
      formData.append('tags', `profile,user_${currentUser.uid}`);

      const interval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(interval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
        {
          method: 'POST',
          body: formData,
        }
      );

      clearInterval(interval);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Upload failed');
      }

      const data = await response.json();
      setUploadProgress(100);
      const imageUrl = data.secure_url;

      const userRef = doc(db, "users", currentUser.uid);
      await updateDoc(userRef, {
        avatar: imageUrl,
        avatar_public_id: data.public_id,
        updatedAt: new Date()
      });

      setProfile(prev => ({ ...prev, avatar: imageUrl }));

    } catch (err) {
      console.error("Error uploading image:", err);
      setError(err.message || 'Failed to upload image. Please try again.');
    } finally {
      setIsUploading(false);
      setTimeout(() => setUploadProgress(0), 1000);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveAvatar = async () => {
    if (!currentUser || !profile.avatar) return;

    setIsUploading(true);
    try {
      const userRef = doc(db, "users", currentUser.uid);
      await updateDoc(userRef, {
        avatar: null,
        avatar_public_id: null
      });

      setProfile(prev => ({ ...prev, avatar: null }));
    } catch (err) {
      console.error("Error removing avatar:", err);
      setError('Failed to remove avatar');
    } finally {
      setIsUploading(false);
    }
  };

  const handleFollow = async () => {
    if (!profile || !currentUser) return;

    try {
      setLoading(true);
      if (isFollowing) {
        await unfollowUser(currentUser.uid, profile.id);
        setIsFollowing(false);
      } else {
        await followUser(currentUser.uid, profile.id);
        setIsFollowing(true);
      }

      const userRef = doc(db, "users", profile.id);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        setFollowersList(prev => {
          if (isFollowing) {
            return prev.filter(user => user.id !== currentUser.uid);
          } else {
            return [...prev, {
              id: currentUser.uid,
              email: currentUser.email,
              username: currentUser.email.split('@')[0]
            }];
          }
        });
      }
    } catch (err) {
      console.error("Follow error:", err);
      setError("Failed to update follow status");
    } finally {
      setLoading(false);
    }
  };

  const handleBioUpdate = async () => {
    if (!currentUser || !profile) return;

    try {
      const userRef = doc(db, "users", currentUser.uid);
      await updateDoc(userRef, {
        bio: newBio,
        updatedAt: new Date()
      });

      setProfile(prev => ({ ...prev, bio: newBio }));
      setProfileData(prev => ({ ...prev, bio: newBio }));
      setIsEditingBio(false);
    } catch (err) {
      console.error("Error updating bio:", err);
      setError('Failed to update bio');
    }
  };

  const handleProfileUpdate = async () => {
    if (!currentUser || !profile) return;

    try {
      const userRef = doc(db, "users", currentUser.uid);
      await updateDoc(userRef, {
        ...profileData,
        updatedAt: new Date()
      });

      setProfile(prev => ({ ...prev, ...profileData }));
      setIsEditingProfile(false);
    } catch (err) {
      console.error("Error updating profile:", err);
      setError('Failed to update profile');
    }
  };

  const formatJoinDate = (date) => {
    if (!date) return "Recently joined";
    const joinDate = date.toDate ? date.toDate() : new Date(date);
    const options = { year: 'numeric', month: 'long' };
    return `Joined ${joinDate.toLocaleDateString('en-US', options)}`;
  };

  // Function to get posts based on active tab
  const getPostsForTab = () => {
    switch (activeTab) {
      case "media":
        return mediaPosts;
      case "likes":
        return likedPosts;
      default:
        return posts;
    }
  };



  // Handle post click to navigate to post detail
  const handlePostClick = (postId) => {
    navigate(`/post/${postId}`);
  };

  if (loading) return <div className="loading">Loading profile...</div>;
  if (error) return <div className="error">{error}</div>;
  if (!profile) return <div className="error">Profile not found</div>;

  const isCurrentUserProfile = profile.email === currentUser?.email;
  const rollNumber = getRollNumberFromEmail(profile.email);
  const joinDate = formatJoinDate(profile.createdAt || profile.joinedAt);
  const currentPosts = getPostsForTab();

  return (
    <div className={styles.container}>
      {/* Editorial Header */}
      <ScrollReveal animation="animate-fade-in-up">
        <div className={styles.header}>
          <div className={styles.avatarSection}>
            <div className={styles.avatarWrapper}>
              <div className={styles.avatarContainer}>
                {profile.avatar ? (
                  <img
                    src={profile.avatar}
                    alt="Profile"
                    className={styles.avatarImage}
                  />
                ) : (
                  <div className="avatar-placeholder">
                    {profile.username?.charAt(0)?.toUpperCase() ||
                      profile.email.charAt(0).toUpperCase()}
                  </div>
                )}

                {isCurrentUserProfile && (
                  <div className={styles.avatarOverlay}>
                    <label htmlFor="avatar-upload" className={styles.avatarUploadBtn}>
                      <FaCamera />
                    </label>
                    <input
                      id="avatar-upload"
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      ref={fileInputRef}
                      disabled={isUploading}
                      style={{ display: 'none' }}
                    />
                  </div>
                )}
              </div>

              {isUploading && (
                <div className="upload-progress">
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className={styles.infoSection}>
            <div className={styles.topRow}>
              <div className={styles.nameSection}>
                <h1 className={styles.displayName}>
                  {profile.displayName || profile.username || rollNumber}
                </h1>
                <span className={styles.username}>
                  @{profile.username || rollNumber}
                </span>
              </div>

              <div className={styles.actions}>
                {isCurrentUserProfile ? (
                  <>
                    <button
                      className={styles.btnOutline}
                      onClick={() => setIsEditingProfile(true)}
                    >
                      Edit profile
                    </button>
                    {profile.avatar && (
                      <button
                        className="remove-avatar-btn"
                        onClick={handleRemoveAvatar}
                        disabled={isUploading}
                      >
                        Remove photo
                      </button>
                    )}
                  </>
                ) : (
                  <>
                    <button
                      className={isFollowing ? styles.btnOutline : styles.btnFollow}
                      onClick={handleFollow}
                      disabled={!currentUser || loading}
                    >
                      {loading ? "..." : isFollowing ? "Following" : "Follow"}
                    </button>
                    <button className={styles.btnOutline}>
                      Message
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Profile Stats - Instagram Style */}
            <div className={styles.stats}>
              <div className={styles.statItem}>
                <strong className={styles.statValue}>{posts.length}</strong>
                <span className={styles.statLabel}>Posts</span>
              </div>
              <div
                className={styles.statItem}
                onClick={() => setShowFollowers(true)}
              >
                <strong className={styles.statValue}>{followersList.length}</strong>
                <span className={styles.statLabel}>Followers</span>
              </div>
              <div
                className={styles.statItem}
                onClick={() => setShowFollowing(true)}
              >
                <strong className={styles.statValue}>{followingList.length}</strong>
                <span className={styles.statLabel}>Following</span>
              </div>
            </div>

            {/* Bio Section - Twitter Style */}
            <div className={styles.bioSection}>
              {isEditingBio ? (
                <div className="bio-editor">
                  {/* ... (Keep bio editor simplified or style it later if needed) ... */}
                  <textarea
                    value={newBio}
                    onChange={(e) => setNewBio(e.target.value)}
                    placeholder="Tell us about yourself..."
                    maxLength={160}
                    className={styles.input} // using auth input style or add one
                    style={{ width: '100%', padding: '8px', marginBottom: '8px' }}
                  />
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button className={styles.btnOutline} onClick={() => setIsEditingBio(false)}>Cancel</button>
                    <button className={styles.btnFollow} onClick={handleBioUpdate}>Save</button>
                  </div>
                </div>
              ) : (
                <div className={styles.bioContent}>
                  <p className={styles.bio}>{profile.bio || "No bio yet"}</p>
                  {isCurrentUserProfile && !profile.bio && (
                    <button className={styles.btnOutline} onClick={() => setIsEditingBio(true)} style={{ fontSize: '0.8rem', padding: '4px 8px' }}>+ Bio</button>
                  )}
                  {isCurrentUserProfile && profile.bio && (
                    <button className={styles.btnOutline} onClick={() => setIsEditingBio(true)} style={{ fontSize: '0.8rem', padding: '4px 8px', marginTop: '4px' }}>Edit</button>
                  )}
                </div>
              )}

              {/* Profile Details */}
              <div className={styles.detailsGrid}>
                {profile.location && (
                  <div className={styles.detailItem}>
                    <FaMapMarkerAlt /> <span>{profile.location}</span>
                  </div>
                )}
                {profile.website && (
                  <div className={styles.detailItem}>
                    <FaLink />
                    <a href={profile.website} target="_blank" rel="noopener noreferrer">
                      {profile.website.replace(/^https?:\/\//, '')}
                    </a>
                  </div>
                )}
                <div className={styles.detailItem}>
                  <FaCalendarAlt /> <span>{joinDate}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

      </ScrollReveal>

      {/* Posts Grid - Instagram Style */}
      <div className={styles.content}>
        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${activeTab === "posts" ? styles.activeTab : ""}`}
            onClick={() => setActiveTab("posts")}
          >
            <span>Posts</span>
          </button>
          <button
            className={`${styles.tab} ${activeTab === "media" ? styles.activeTab : ""}`}
            onClick={() => setActiveTab("media")}
          >
            <span>Media</span>
          </button>
          <button
            className={`${styles.tab} ${activeTab === "likes" ? styles.activeTab : ""}`}
            onClick={() => setActiveTab("likes")}
          >
            <span>Likes</span>
          </button>
        </div>

        {currentPosts.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>
              {activeTab === "media" ? <FaCamera /> :
                activeTab === "likes" ? <FaHeart /> :
                  <FaEdit />}
            </div>
            <h3>
              {activeTab === "media"
                ? "No Media Yet"
                : activeTab === "likes"
                  ? "No Likes Yet"
                  : "No Posts Yet"}
            </h3>
            <p>
              {activeTab === "media"
                ? "When you share photos or videos, they will appear here."
                : activeTab === "likes"
                  ? "Posts you like will appear here."
                  : "When you share posts, they will appear here."}
            </p>
            {isCurrentUserProfile && activeTab === "posts" && (
              <button
                className="create-first-post"
                onClick={() => navigate("/create-post")}
              >
                Create your first post
              </button>
            )}
          </div>
        ) : (
          <div className={activeTab === 'media' ? styles.grid : styles.feedList}>
            {currentPosts.map((post) => (
              <div
                key={post.id}
                className={activeTab === 'media' ? styles.gridItem : styles.postCard}
                onClick={() => handlePostClick(post.id)}
              >
                {/* Visual Logic based on view */}
                {activeTab === 'media' ? (
                  <>
                    {(post.files && post.files.length > 0) ? (
                      <>
                        <img src={post.files[0].url} alt="Post" className={styles.gridImage} />
                        <div className={styles.gridOverlay}>
                          <span><FaHeart /> {post.likes?.length || 0}</span>
                        </div>
                      </>
                    ) : (
                      <div className={styles.gridOverlay} style={{ opacity: 1, background: 'var(--color-bg-subtle)', color: 'var(--color-text-main)' }}>
                        <span style={{ fontSize: '0.8rem', padding: '8px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{post.text?.substring(0, 50)}...</span>
                      </div>
                    )}
                  </>
                ) : (
                  // List View (Posts/Likes)
                  <>
                    <div className={styles.postHeader}>
                      <span>{post.createdAt?.toLocaleDateString()}</span>
                    </div>
                    <p className={styles.postText}>{post.text}</p>
                    {post.files && post.files.length > 0 && (
                      <img src={post.files[0].url} alt="Post" style={{ width: '100%', borderRadius: '4px', maxHeight: '300px', objectFit: 'cover' }} />
                    )}
                    <div className={styles.postInteractions}>
                      <span><FaHeart /> {post.likes?.length || 0}</span>
                      <span><FaComment /> {post.comments?.length || 0}</span>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit Profile Modal */}
      {isEditingProfile && (
        <div className="modal-overlay" onClick={() => setIsEditingProfile(false)}>
          <div className="modal-content edit-profile-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Profile</h3>
              <button className="close-btn" onClick={() => setIsEditingProfile(false)}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Display Name</label>
                <input
                  type="text"
                  value={profileData.displayName || ""}
                  onChange={(e) => setProfileData(prev => ({ ...prev, displayName: e.target.value }))}
                  placeholder="Display name"
                />
              </div>
              <div className="form-group">
                <label>Bio</label>
                <textarea
                  value={profileData.bio || ""}
                  onChange={(e) => setProfileData(prev => ({ ...prev, bio: e.target.value }))}
                  placeholder="Tell us about yourself..."
                  maxLength={160}
                />
                <div className="char-count">{profileData.bio?.length || 0}/160</div>
              </div>
              <div className="form-group">
                <label>Location</label>
                <input
                  type="text"
                  value={profileData.location || ""}
                  onChange={(e) => setProfileData(prev => ({ ...prev, location: e.target.value }))}
                  placeholder="Location"
                />
              </div>
              <div className="form-group">
                <label>Website</label>
                <input
                  type="url"
                  value={profileData.website || ""}
                  onChange={(e) => setProfileData(prev => ({ ...prev, website: e.target.value }))}
                  placeholder="https://example.com"
                />
              </div>
              <div className="modal-actions">
                <button className="cancel-btn" onClick={() => setIsEditingProfile(false)}>
                  Cancel
                </button>
                <button className="save-btn" onClick={handleProfileUpdate}>
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* Followers Modal */}
      {showFollowers && (
        <div className={styles.modalOverlay} onClick={() => setShowFollowers(false)}>
          <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Followers</h3>
              <button className={styles.closeBtn} onClick={() => setShowFollowers(false)}>×</button>
            </div>
            <div className={styles.userList}>
              {followersList.length > 0 ? (
                followersList.map(user => (
                  <div key={user.id} className={styles.userItem} onClick={() => {
                    navigate(`/profile/${encodeURIComponent(user.email)}`);
                    setShowFollowers(false);
                  }}>
                    <div className={styles.userAvatar}>
                      {user.avatar ? (
                        <img src={user.avatar} alt={user.username} />
                      ) : (
                        <div className={styles.avatarPlaceholder}>
                          {user.username?.charAt(0).toUpperCase() || "U"}
                        </div>
                      )}
                    </div>
                    <div className={styles.userInfo}>
                      <span className={styles.userName}>{user.displayName || user.username}</span>
                      <span className={styles.userHandle}>@{user.username || user.email.split('@')[0]}</span>
                    </div>
                    {currentUser?.uid !== user.id && (
                      <button className={styles.smallFollowBtn} onClick={(e) => {
                        e.stopPropagation();
                        // Logic for follow would go here
                      }}>
                        Follow
                      </button>
                    )}
                  </div>
                ))
              ) : (
                <div className={styles.emptyList}>No followers yet</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Following Modal */}
      {showFollowing && (
        <div className={styles.modalOverlay} onClick={() => setShowFollowing(false)}>
          <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Following</h3>
              <button className={styles.closeBtn} onClick={() => setShowFollowing(false)}>×</button>
            </div>
            <div className={styles.userList}>
              {followingList.length > 0 ? (
                followingList.map(user => (
                  <div key={user.id} className={styles.userItem} onClick={() => {
                    navigate(`/profile/${encodeURIComponent(user.email)}`);
                    setShowFollowing(false);
                  }}>
                    <div className={styles.userAvatar}>
                      {user.avatar ? (
                        <img src={user.avatar} alt={user.username} />
                      ) : (
                        <div className={styles.avatarPlaceholder}>
                          {user.username?.charAt(0).toUpperCase() || "U"}
                        </div>
                      )}
                    </div>
                    <div className={styles.userInfo}>
                      <span className={styles.userName}>{user.displayName || user.username}</span>
                      <span className={styles.userHandle}>@{user.username || user.email.split('@')[0]}</span>
                    </div>
                    {currentUser?.uid !== user.id && (
                      <button className={styles.smallFollowingBtn} onClick={(e) => {
                        e.stopPropagation();
                        // Logic for unfollow would go here
                      }}>
                        Following
                      </button>
                    )}
                  </div>
                ))
              ) : (
                <div className={styles.emptyList}>Not following anyone yet</div>
              )}
            </div>
          </div>
        </div>
      )}



    </div>
  );
}